<template>
  <b-card>
    <div class="mb-2 d-flex align-items-center">
      <span class="mr-2 font-weight-bold">X-Axis:</span>
      <b-form-radio-group
        v-model="localXAxisMode"
        :options="xAxisOptions"
        button-variant="outline-primary"
        size="sm"
        buttons
        @input="updateSetting('xAxisMode', $event)"
      />
      <template v-if="localXAxisMode === 'maturity'">
        <span class="ml-3 mr-2 font-weight-bold">Maturity Mode:</span>
        <b-form-radio-group
          v-model="localMaturityMode"
          :options="maturityModeOptions"
          button-variant="outline-primary"
          size="sm"
          buttons
          @input="updateSetting('maturityMode', $event)"
        />
      </template>
    </div>
    <div v-if="loading">
      <loading-container />
    </div>
    <div v-else-if="!visualContent || !visualContent.CHARTDATA || visualContent.CHARTDATA.length === 0">
      <no-data-container message="No visual data available for the current selection" />
    </div>
    <app-echart-scatter
      v-else-if="isChartReady && scatterChartData"
      ref="chart"
      :key="chartKey"
      :option-data="scatterChartData"
      style="width: 100%; height: 500px;"
    />
  </b-card>
</template>

<script>
import { BCard, BFormRadioGroup } from 'bootstrap-vue'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import NoDataContainer from '@/views/components/NoDataContainer.vue'
import AppEchartScatter from '@core/components/charts/echart/AppEchartScatter.vue'
import useAppConfig from '@core/app-config/useAppConfig'

export default {
  components: {
    BCard,
    BFormRadioGroup,
    LoadingContainer,
    NoDataContainer,
    AppEchartScatter,
  },
  props: {
    visualContent: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    visualSettings: {
      type: Object,
      required: true,
    },
    xAxisOptions: {
      type: Array,
      required: true,
    },
    maturityModeOptions: {
      type: Array,
      required: true,
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      scatterChartData: null,
      scatterMetadata: [],
      seriesConfig: [],
      chartKey: 0,
      isChartReady: false,
      localXAxisMode: this.visualSettings?.xAxisMode || 'maturity',
      localMaturityMode: this.visualSettings?.maturityMode || 'first',
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
  },
  watch: {
    'visualSettings.xAxisMode': {
      handler(newVal) {
        this.localXAxisMode = newVal
      },
      immediate: true,
    },
    'visualSettings.maturityMode': {
      handler(newVal) {
        this.localMaturityMode = newVal
      },
      immediate: true,
    },
    visualContent: {
      handler(data) {
        if (data && data.CHARTDATA) {
          this.processScatterData(data)
        }
      },
      immediate: true,
      deep: true,
    },
    skin() {
      // Re-process data when theme changes
      if (this.visualContent && this.visualContent.CHARTDATA) {
        this.processScatterData(this.visualContent)
      }
    },
  },
  mounted() {
    // Add window resize listener
    window.addEventListener('resize', this.resizeChart)
  },
  activated() {
    // Resize chart and reattach click handler when tab becomes visible
    setTimeout(() => {
      this.resizeChart()
      this.attachClickHandler()
    }, 100)
  },
  beforeDestroy() {
    // Remove window resize listener
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    processScatterData(data) {
      if (!data || !data.CHARTDATA) return

      // Force unmount of chart
      this.isChartReady = false

      // Custom arrow proportions
      const stemWidth = 0.6
      const headWidth = 1.6
      const headHeight = 4.4
      const stemHeight = 10

      // Function to create arrow path with fixed stem height (pointing up)
      const createArrowPath = () => `path://M -${stemWidth},${stemHeight} L -${stemWidth},0 L -${headWidth},0 L 0,-${headHeight} L ${headWidth},0 L ${stemWidth},0 L ${stemWidth},${stemHeight} Z`

      // Function to create rotated arrow path (pointing down)
      const createRotatedArrowPath = () => `path://M -${stemWidth},-${stemHeight} L -${stemWidth},0 L -${headWidth},0 L 0,${headHeight} L ${headWidth},0 L ${stemWidth},0 L ${stemWidth},-${stemHeight} Z`

      // Function to calculate symbol size
      const calculateSymbolSize = () => {
        const arrowTotalWidth = headWidth * 2
        const arrowTotalHeight = stemHeight + headHeight
        return [arrowTotalWidth * 2.5, arrowTotalHeight * 1.5]
      }

      // Define product type styling - using YIELDNAME values from backend
      const productTypeStyles = {
        // Autocall variants
        'Income AC': { color: '#0066FF', name: 'Income AC' },
        'Growth AC': { color: '#3399FF', name: 'Growth AC' },
        AC: { color: '#0066FF', name: 'AC' },

        // Growth products
        'Capped Growth': { color: '#90EE90', name: 'Capped Growth' },
        'Uncapped Growth': { color: '#90EE90', name: 'Uncapped Growth' },

        // Income products
        Income: { color: '#00cfe8', name: 'Income' },
        'Contingent Income': { color: '#00cfe8', name: 'Contingent Income' },

        // Digital
        Digital: { color: '#28c76f', name: 'Digital' },

        // Other types
        'Reverse Convertible': { color: '#FFA500', name: 'Reverse Convertible' },
        Booster: { color: '#9370DB', name: 'Booster' },

        // Barrier (downward)
        Barrier: {
          color: '#ea5455',
          name: 'Barrier',
        },
      }

      const series = []
      const scatterMeta = []

      // Process CHARTDATA - group by product type
      if (data.CHARTDATA && data.CHARTDATA.length > 0) {
        // Group points by product type and level type
        const groupedData = {}

        data.CHARTDATA.forEach(item => {
          const x = item.XVALUE
          const yieldType = item.YIELDNAME || item.YIELDTYPE || 'Unknown'

          // Add autocall level point if it exists
          if (item.HASFIRSTUPSIDE && item.FIRSTUPSIDELEVEL !== null && item.FIRSTUPSIDELEVEL !== undefined) {
            const key = yieldType
            if (!groupedData[key]) {
              groupedData[key] = { data: [], metadata: [] }
            }
            const symbolSize = calculateSymbolSize()
            groupedData[key].data.push({
              value: [x, item.FIRSTUPSIDELEVEL],
              symbol: createArrowPath(),
              symbolSize,
              symbolOffset: [0, -symbolSize[1] / 2], // Shift up so base is at y-point
            })
            groupedData[key].metadata.push({
              ...item,
              levelType: 'Autocall Level',
              levelValue: item.FIRSTUPSIDELEVEL,
            })
          }

          // Add digital/final upside level point if it exists (for products without autocalls)
          if (item.HASFINALUPSIDE && item.FINALUPSIDELEVEL !== null && item.FINALUPSIDELEVEL !== undefined && !item.HASFIRSTUPSIDE) {
            const key = yieldType
            if (!groupedData[key]) {
              groupedData[key] = { data: [], metadata: [] }
            }
            const symbolSize = calculateSymbolSize()
            groupedData[key].data.push({
              value: [x, item.FINALUPSIDELEVEL],
              symbol: createArrowPath(),
              symbolSize,
              symbolOffset: [0, -symbolSize[1] / 2], // Shift up so base is at y-point
            })
            groupedData[key].metadata.push({
              ...item,
              levelType: 'Digital Level',
              levelValue: item.FINALUPSIDELEVEL,
            })
          }

          // Add barrier level point if it exists
          if (item.HASBARRIER && item.BARRIER !== null && item.BARRIER !== undefined) {
            const key = 'Barrier'
            if (!groupedData[key]) {
              groupedData[key] = { data: [], metadata: [] }
            }
            const symbolSize = calculateSymbolSize()
            groupedData[key].data.push({
              value: [x, item.BARRIER],
              symbol: createArrowPath(),
              symbolSize,
              symbolRotate: 180,
              symbolOffset: [0, symbolSize[1] / 2], // Shift down so base (top after rotation) is at y-point
            })
            groupedData[key].metadata.push({
              ...item,
              levelType: 'Barrier Level',
              levelValue: item.BARRIER,
            })
          }
        })

        // Create series for each product type
        let metadataIndex = 0
        const symbolSize = calculateSymbolSize()

        Object.keys(groupedData).forEach(productType => {
          const group = groupedData[productType]
          const style = productTypeStyles[productType]

          // Log unmapped product types for debugging
          if (!style) {
            console.log('Unmapped product type:', productType)
          }

          const finalStyle = style || {
            color: '#FF00FF', // Bright magenta for unmapped types
            name: productType,
          }

          // Store metadata with proper indices
          group.metadata.forEach(meta => {
            scatterMeta.push(meta)
          })

          // Barrier series needs 180 degree rotation
          const isBarrier = productType === 'Barrier'

          const seriesConfig = {
            name: finalStyle.name,
            type: 'scatter',
            data: group.data.map(d => d.value), // Extract just the [x,y] values
            symbol: createArrowPath(),
            symbolSize,
            symbolOffset: isBarrier ? [0, symbolSize[1] / 2] : [0, -symbolSize[1] / 2],
            symbolRotate: isBarrier ? 180 : 0,
            itemStyle: {
              color: finalStyle.color,
            },
            emphasis: {
              itemStyle: {
                cursor: 'pointer',
              },
            },
            label: {
              show: false,
            },
            // Store starting index for this series in metadata array
            metadataStartIndex: metadataIndex,
          }

          series.push(seriesConfig)
          metadataIndex += group.data.length
        })

        // Store metadata in component data for tooltip access
        this.scatterMetadata = scatterMeta
        this.seriesConfig = series // Store for tooltip lookup
      }

      // Process VECTORDATA - add vector lines
      if (data.VECTORDATA && data.VECTORDATA.length > 0) {
        const vectorColors = ['#FF1493', '#FFD700', '#00CED1', '#FF6347', '#9370DB', '#32CD32']
        data.VECTORDATA.forEach((vector, index) => {
          if (vector.data && vector.data.length > 0) {
            series.push({
              name: vector.name || `Vector ${index + 1}`,
              type: 'line',
              data: vector.data,
              lineStyle: {
                color: vector.color || vectorColors[index % vectorColors.length],
                width: 2,
              },
              symbol: 'none',
              smooth: false,
            })
          }
        })
      }

      // Process ANNOTATIONDATA - add annotation lines or markers
      if (data.ANNOTATIONDATA && data.ANNOTATIONDATA.length > 0) {
        data.ANNOTATIONDATA.forEach((annotation, index) => {
          if (annotation.type === 'line' && annotation.data) {
            series.push({
              name: annotation.label || `Annotation ${index + 1}`,
              type: 'line',
              data: annotation.data,
              lineStyle: {
                color: annotation.color || '#ea5455',
                width: annotation.width || 2,
                type: annotation.style || 'solid',
              },
              symbol: 'none',
              silent: true,
              legendHoverLink: false,
            })
          }
        })
      }

      // Build maturity clusters from actual product data
      // Group products by their actual maturity to find cluster centers
      const maturityClusters = {}
      if (data.CHARTDATA && data.CHARTDATA.length > 0) {
        data.CHARTDATA.forEach(item => {
          const actualMaturity = item.SELECTEDMATURITY
          const xPos = item.XVALUE

          if (!maturityClusters[actualMaturity]) {
            maturityClusters[actualMaturity] = {
              positions: [],
              maturity: actualMaturity,
            }
          }
          maturityClusters[actualMaturity].positions.push(xPos)
        })
      }

      // Calculate cluster centers and create labels
      const maturityLabels = []
      Object.values(maturityClusters).forEach(cluster => {
        // Find center position of this maturity cluster
        const avgPos = cluster.positions.reduce((sum, pos) => sum + pos, 0) / cluster.positions.length
        maturityLabels.push({
          value: avgPos,
          label: `${cluster.maturity}`,
          actualMaturity: cluster.maturity,
        })
      })

      // Sort labels by position
      maturityLabels.sort((a, b) => a.value - b.value)

      // Track which labels have been shown to prevent duplicates
      const shownLabels = new Set()
      let lastTickValue = -Infinity // Track last tick to detect new render cycles

      // Calculate axis ranges
      let xMin = Infinity
      let xMax = -Infinity
      let yMin = Infinity
      let yMax = -Infinity

      if (data.CHARTDATA && data.CHARTDATA.length > 0) {
        data.CHARTDATA.forEach(point => {
          const x = point.XVALUE

          if (x !== undefined && x !== null && !Number.isNaN(x)) {
            if (x < xMin) xMin = x
            if (x > xMax) xMax = x
          }

          // Check all possible y values (multiple levels per product)
          const yValues = []
          if (point.HASFIRSTUPSIDE && point.FIRSTUPSIDELEVEL !== null && point.FIRSTUPSIDELEVEL !== undefined) {
            yValues.push(point.FIRSTUPSIDELEVEL)
          }
          if (point.HASBARRIER && point.BARRIER !== null && point.BARRIER !== undefined) {
            yValues.push(point.BARRIER)
          }
          if (point.HASFINALUPSIDE && point.FINALUPSIDELEVEL !== null && point.FINALUPSIDELEVEL !== undefined) {
            yValues.push(point.FINALUPSIDELEVEL)
          }

          yValues.forEach(y => {
            if (!Number.isNaN(y)) {
              if (y < yMin) yMin = y
              if (y > yMax) yMax = y
            }
          })
        })
      }

      // Handle case where no valid points were found
      if (!Number.isFinite(xMin)) xMin = 0
      if (!Number.isFinite(xMax)) xMax = 100
      if (!Number.isFinite(yMin)) yMin = 0
      if (!Number.isFinite(yMax)) yMax = 100

      // Add minimal padding to the axis ranges - just enough for the arrows
      // For single point charts, use larger fixed padding to center properly
      const xPadding = xMax === xMin ? Math.max(2, xMax * 0.1) : (xMax - xMin) * 0.02

      // Separate barrier series from others for legend grouping
      const barrierSeries = series.filter(s => s.name === 'Barrier')
      const nonBarrierSeries = series.filter(s => s.name !== 'Barrier')

      const self = this
      this.scatterChartData = {
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: {
            type: 'cross',
            crossStyle: {
              color: '#999',
              type: 'dashed',
            },
            snap: true,
            label: {
              show: false,
            },
          },
          formatter(params) {
            if (Array.isArray(params) && params.length > 0) {
              // Group scatter points by product and collect vector lines
              const productMap = {}
              const vectorLines = []

              params.forEach(param => {
                if (param.componentSubType === 'scatter') {
                  // Find the correct metadata index for this series
                  const seriesData = self.seriesConfig.find(s => s.name === param.seriesName)
                  if (seriesData && self.scatterMetadata) {
                    const metadataIndex = seriesData.metadataStartIndex + param.dataIndex
                    const point = self.scatterMetadata[metadataIndex]

                    if (point) {
                      // Use reportnumber as key to uniquely identify each product
                      const key = point.REPORTNUMBER

                      if (!productMap[key]) {
                        productMap[key] = {
                          reportNumber: point.REPORTNUMBER,
                          provider: point.PROVIDER,
                          productName: point.REPORTTITLE,
                          yieldname: point.YIELDNAME,
                          type: point.PRODUCTTYPE,
                          maturity: point.SELECTEDMATURITY,
                          yield: point.YIELD,
                          levels: [],
                        }
                      }
                      productMap[key].levels.push({
                        type: point.levelType,
                        value: param.data[1],
                      })
                    }
                  }
                } else if (param.componentSubType === 'line') {
                  vectorLines.push(`${param.seriesName}: ${param.data[1]}%`)
                }
              })

              // Build tooltip content
              let tooltipContent = ''
              Object.values(productMap).forEach(product => {
                tooltipContent += `<b>${product.reportNumber || ''} ${product.provider || 'Product'}</b><br/>`
                if (product.productName) tooltipContent += `${product.productName}<br/>`
                if (product.type) tooltipContent += `Type: ${product.type}<br/>`
                tooltipContent += `Maturity: ${product.maturity} years<br/>`
                tooltipContent += `Yield: ${product.yield}%<br/>`
                product.levels.forEach(level => {
                  tooltipContent += `${level.type}: ${level.value}%<br/>`
                })
                tooltipContent += '<br/>'
              })

              // Add vector lines if any
              if (vectorLines.length > 0) {
                tooltipContent += vectorLines.join('<br/>')
              }

              return tooltipContent || 'No data'
            }
            return 'No data'
          },
        },
        legend: [
          {
            show: true,
            left: barrierSeries.length > 0 ? '20%' : 'center',
            top: 0,
            data: nonBarrierSeries.map(s => s.name),
          },
          {
            show: barrierSeries.length > 0,
            left: 'auto',
            right: '26%',
            top: 0,
            data: [{
              name: 'Barrier',
              icon: createRotatedArrowPath(),
            }],
          },
        ],
        grid: {
          left: 80,
          right: 30,
          bottom: 60,
          top: 70,
          containLabel: false,
        },
        xAxis: {
          type: 'value',
          name: data.xAxisLabel || 'Maturity (Years)',
          nameLocation: 'middle',
          nameGap: 35,
          min: data.xAxisLabel && data.xAxisLabel.includes('Yield') ? -0.2 : xMin - xPadding,
          max: data.xAxisLabel && data.xAxisLabel.includes('Yield') ? undefined : xMax + xPadding,
          interval: data.xAxisLabel && data.xAxisLabel.includes('Yield') ? 2 : undefined,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
            margin: data.xAxisLabel && data.xAxisLabel.includes('Yield') ? 12 : 8,
            showMinLabel: data.xAxisLabel && data.xAxisLabel.includes('Yield'),
            showMaxLabel: false,
            formatter(value) {
              // For yield mode, show integer values without %
              if (data.xAxisLabel && data.xAxisLabel.includes('Yield')) {
                // Hide negative offset values
                if (value < 0) return ''
                return Math.round(value).toString()
              }

              // For maturity mode, use custom maturity labels
              // Detect new render cycle (ticks starting over) and reset shown labels
              if (value < lastTickValue) {
                shownLabels.clear()
              }
              lastTickValue = value

              // Find the closest cluster center
              if (maturityLabels.length === 0) return ''

              let closest = maturityLabels[0]
              let minDiff = Math.abs(value - closest.value)

              maturityLabels.forEach(ml => {
                const diff = Math.abs(value - ml.value)
                if (diff < minDiff) {
                  minDiff = diff
                  closest = ml
                }
              })

              // Use moderate tolerance based on expected tick spacing
              const avgTickSpacing = (xMax - xMin) / 80
              // Use minimum tolerance for single-point or very small range charts
              const tolerance = Math.max(avgTickSpacing * 0.6, 0.1)

              // Only show label if within tolerance and not already shown
              if (minDiff < tolerance && !shownLabels.has(closest.actualMaturity)) {
                shownLabels.add(closest.actualMaturity)
                return closest.label
              }
              return ''
            },
          },
          axisTick: {
            show: false,
          },
          axisLine: {
            show: true,
            lineStyle: {
              width: 1,
            },
          },
          splitNumber: data.xAxisLabel && data.xAxisLabel.includes('Yield') ? undefined : 80,
          scale: data.xAxisLabel && data.xAxisLabel.includes('Yield'),
          show: true,
          splitLine: {
            show: false,
          },
          axisPointer: {
            show: true,
            snap: true,
            label: {
              formatter(params) {
                // For yield mode, show integer value without %
                if (data.xAxisLabel && data.xAxisLabel.includes('Yield')) {
                  return Math.round(params.value).toString()
                }

                // For maturity mode, show actual maturity in the axis pointer tooltip
                if (maturityLabels.length === 0) return params.value.toFixed(1)

                let closest = maturityLabels[0]
                let minDiff = Math.abs(params.value - closest.value)

                maturityLabels.forEach(ml => {
                  const diff = Math.abs(params.value - ml.value)
                  if (diff < minDiff) {
                    minDiff = diff
                    closest = ml
                  }
                })

                return closest.label
              },
            },
          },
        },
        yAxis: {
          type: 'value',
          name: data.yAxisLabel || 'Underlying Level (%)',
          nameLocation: 'middle',
          nameGap: 50,
          min: 0,
          max: 150,
          interval: 25,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
            formatter: value => value.toFixed(0),
          },
          axisLine: {
            show: false,
          },
          scale: false,
          show: true,
          splitLine: {
            show: true,
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
          axisPointer: {
            show: true,
            snap: false,
          },
        },
        series,
      }

      // Increment key to force chart re-render
      this.chartKey += 1

      // Use nextTick to ensure the chart remounts after data is ready
      this.$nextTick(() => {
        this.isChartReady = true
        // Wait for chart to fully mount before attaching handler
        setTimeout(() => {
          this.resizeChart()
          this.attachClickHandler()
        }, 100)
      })
    },
    resizeChart() {
      if (this.$refs.chart) {
        this.$refs.chart.resize()
      }
    },
    updateSetting(key, value) {
      // Emit event to parent to update settings
      this.$emit('update-visual-settings', { key, value })
    },
    attachClickHandler() {
      if (this.$refs.chart) {
        const chartInstance = this.$refs.chart.getChartInstance()
        if (chartInstance) {
          // Remove any existing click handlers to avoid duplicates
          chartInstance.off('click')

          // Add click handler for scatter points
          chartInstance.on('click', params => {
            // Only handle clicks on scatter series (not lines or annotations)
            if (params.componentSubType === 'scatter' && params.seriesName) {
              // Find the metadata for this point
              const seriesData = this.seriesConfig.find(s => s.name === params.seriesName)
              if (seriesData && this.scatterMetadata) {
                const metadataIndex = seriesData.metadataStartIndex + params.dataIndex
                const point = this.scatterMetadata[metadataIndex]

                if (point && point.REPORTNUMBER) {
                  // Navigate to the product detail page
                  this.$router.push({
                    name: 'product-report',
                    params: {
                      reportNumber: point.REPORTNUMBER,
                    },
                  })
                }
              }
            }
          })
        }
      }
    },
  },
}
</script>
